/**
 * Driving DAT service V2 — calls the uniapp-backend DAT endpoints
 * instead of the legacy n8n webhooks.
 *
 * Same method signatures as `dat.js` so existing pages can swap the
 * import path without changing call-site code (mostly). The differences
 * are in the RETURN shape:
 *
 * - V1 (n8n): returns raw — array OR object, no envelope.
 *     Example: `await datService.search(params)` → `[ {session_id, ...}, ...]`
 *
 * - V2 (uniapp): returns `{ data, pagination, message }` (auto-unwrapped
 *   by uniappClient interceptor).
 *     Example: `await datV2.search(params)` → `{ data: [...], pagination: {...}, message: 'success' }`
 *
 * Migration pattern in a page:
 *   ```js
 *   // V1
 *   const docs = await datService.search({ name });
 *
 *   // V2
 *   const { data: docs, pagination } = await datV2.search({ name });
 *   ```
 *
 * All paths prefixed with `/v2/dat/` — coexists with legacy V1 `/dat/`
 * on the same uniapp BE (different controller, different Mongo collection).
 */
import uniappClient from './uniappClient';

// Default center for the current single-tenant deployment. Override
// via env if the FE needs to drive multiple centers from one Vercel
// deployment (not currently the case).
const DEFAULT_CENTER =
  process.env.REACT_APP_UNIAPP_DAT_CENTER ||
  '67b4559db586fd615075e72e'; // Trung tâm GDNN Phú Giáo

class DatServiceV2 {
  constructor() {
    this.dat = uniappClient;
    this.center = DEFAULT_CENTER;
  }

  // ---- read --------------------------------------------------------------

  /** GET /v2/dat?center=…&checked=false&page=&limit= */
  async query(params = {}) {
    return this.dat.get('/v2/dat', { params: { center: this.center, ...params } });
  }

  /** GET /v2/dat/search?name=&drivingClass=&page=&limit= */
  async search(params = {}) {
    return this.dat.get('/v2/dat/search', { params: { center: this.center, ...params } });
  }

  /** GET /v2/dat/search/final?name=&drivingClass=&page=&limit= */
  async searchFinalResult(params = {}) {
    return this.dat.get('/v2/dat/search/final', { params: { center: this.center, ...params } });
  }

  /** GET /v2/dat/search/student?name=&page=&limit= */
  async searchStudent(params = {}) {
    return this.dat.get('/v2/dat/search/student', { params: { center: this.center, ...params } });
  }

  /** GET /v2/dat/calc?center=…  — heavy aggregate, also mutates dat_students. */
  async calcTotalDistance() {
    return this.dat.get('/v2/dat/calc', { params: { center: this.center } });
  }

  /** GET /v2/dat/student/first-check?hang=BSS,BTĐ,C1 */
  async searchFirstCheckStudent(params = {}) {
    return this.dat.get('/v2/dat/student/first-check', { params: { center: this.center, ...params } });
  }

  /** GET /v2/dat/student/second-check?hang=BSS,BTĐ,C1 */
  async searchSecondCheckStudent(params = {}) {
    return this.dat.get('/v2/dat/student/second-check', { params: { center: this.center, ...params } });
  }

  /** GET /v2/dat/session?sessionId=… */
  async getBySessionId(sessionId) {
    return this.dat.get('/v2/dat/session', { params: { center: this.center, sessionId } });
  }

  // ---- writes ------------------------------------------------------------

  /**
   * PATCH /dat — update session by sessionId.
   * Body whitelist: kq / violation / checked / excelSync / processed /
   *   calculated / manualNightDuration / warning.
   *
   * Note (camelCase): V2 uses camelCase keys. V1 used snake_case
   * (`excel_sync`, `manual_night_duration`). Call sites must rename
   * when migrating.
   */
  async update(payload) {
    return this.dat.patch('/v2/dat', { center: this.center, ...payload });
  }

  /**
   * PATCH /dat/student — update student review flags.
   * Body: { registrationCode, firstChecked?, secondChecked?, allChecked?,
   *   note?, totalDistance?, distanceCondition? }
   *
   * V1 used `Mã đăng ký` as the lookup key; V2 uses `registrationCode`.
   */
  async updateStudent(payload) {
    return this.dat.patch('/v2/dat/student', { center: this.center, ...payload });
  }

  /**
   * POST /dat/bulk — bulk upsert sessions. Each row needs `sessionId`.
   * Sessions schema is camelCase: see uniapp DatSession model.
   */
  async bulkUpsertSessions(rows) {
    return this.dat.post('/v2/dat/bulk', { center: this.center, rows });
  }

  /** DELETE /v2/dat/session?sessionId=… — soft delete. */
  async deleteSession(sessionId) {
    return this.dat.delete('/v2/dat/session', { params: { center: this.center, sessionId } });
  }

  // ---- sync from external systems ---------------------------------------

  /**
   * POST /dat/sync/{provider}  — TP (SLX Jira) or Ecotek.
   * Body: { startDate, endDate, maxPages? } (YYYY-MM-DD)
   * Provider must match the route exactly: `TP` or `Ecotek` (case-sensitive).
   */
  async sync(provider, startDate, endDate, extra = {}) {
    return this.dat.post(`/v2/dat/sync/${provider}`, {
      center: this.center,
      startDate,
      endDate,
      ...extra,
    });
  }

  /** POST /dat/sync/student — mirror students from uniapp Driving. */
  async syncStudent() {
    return this.dat.post('/v2/dat/sync/student', { center: this.center });
  }

  /**
   * PATCH /dat/tp/mark — mark a session pass/fail on SLX Jira side.
   * Body: { id, mark }
   */
  async markTPDat(id, mark) {
    return this.dat.patch('/v2/dat/tp/mark', { center: this.center, id, mark });
  }

  /** POST /v2/dat/check-error — run all 3 audits (under5min, lowFaceId, shortBreaks). */
  async checkError() {
    return this.dat.post('/v2/dat/check-error', { center: this.center });
  }

  // ---- action-audit log --------------------------------------------------

  /**
   * GET /v2/dat/action-logs?center=&page=&limit=&action=&actorId=&from=&to=&q=
   *
   * `q` tìm trên chính dữ liệu log (entityId / summary / actorName) —
   * không phân giải danh tính. Log `update.session` chỉ lưu UUID phiên
   * nên gõ mã học viên sẽ không ra nhóm log đó; xem chú thích dưới ô tìm.
   */
  async getActionLogs(params = {}) {
    return this.dat.get('/v2/dat/action-logs', { params: { center: this.center, ...params } });
  }

  /**
   * GET /v2/dat/session?sessionId= — 1 phiên DAT theo UUID. Dùng ở modal
   * nhật ký để đi từ `entityId` của log `update.session` ra mã học viên.
   */
  async getSessionById(sessionId) {
    return this.dat.get('/v2/dat/session', {
      params: { center: this.center, sessionId },
    });
  }

  /**
   * GET /user/:id — resolve a uniapp user (the audit-log actor) to their
   * detail. Returns the safe whitelist { _id, name, userName, email,
   * phone, role, zalo, center, store, createdAt }. Requires an admin JWT
   * (same one uniappClient already attaches). Used by the audit-log
   * "Chi tiết người thực hiện" modal.
   */
  async getUserById(userId) {
    return this.dat.get(`/user/${userId}`);
  }

  /**
   * POST /v2/dat/approve-result — phê duyệt kết quả DAT của 1 học viên
   * và đẩy sang field GetFly. `result` là 'pass' | 'fail' (FE tự tính từ
   * checkDATConditions). BE tự tra account_id GetFly từ registrationCode.
   * Trả { data: { registrationCode, gfAccountId, result, syncedAt } }.
   */
  async approveDatResult(registrationCode, result) {
    return this.dat.post('/v2/dat/approve-result', {
      center: this.center,
      registrationCode,
      result,
    });
  }

  /**
   * POST /v2/dat/submit-for-approval — người CHECK (DAT_ADMIN) gửi 1 HV cho
   * approver phê duyệt (sau khi đã check xong hết phiên).
   */
  async submitForApproval(registrationCode) {
    return this.dat.post('/v2/dat/submit-for-approval', {
      center: this.center,
      registrationCode,
    });
  }

  /**
   * POST /v2/dat/reject-approval — người DUYỆT (DAT_APPROVER) trả về 1 HV đã
   * gửi kèm lý do → về 'rejected' để người check sửa & gửi lại.
   */
  async rejectApproval(registrationCode, reason) {
    return this.dat.post('/v2/dat/reject-approval', {
      center: this.center,
      registrationCode,
      reason,
    });
  }

  /**
   * GET /v2/dat/student/summary?registrationCode= — BE tính kết quả DAT
   * theo TỪNG học viên (group phiên, cộng tổng, so ngưỡng). FE chỉ hiển thị.
   * Trả { data: { registrationCode, license, totals, details, valid,
   *   allChecked, message, sessionCount } }.
   */
  async getStudentSummary(registrationCode) {
    return this.dat.get('/v2/dat/student/summary', {
      params: { center: this.center, registrationCode },
    });
  }

  /**
   * GET /v2/dat/course/students?courseCode= — liệt kê học viên 1 khoá để
   * đồng bộ kết quả DAT hàng loạt. `courseCode` khớp cả mã khoá CSDL lẫn
   * courseShortName (vd BTĐ-K35). Trả { data: { courseCode, count, students } }.
   */
  async getCourseStudents(courseCode, { withConclusion = false } = {}) {
    return this.dat.get('/v2/dat/course/students', {
      params: { center: this.center, courseCode, ...(withConclusion ? { withConclusion: 1 } : {}) },
    });
  }

  /**
   * POST /v2/dat/approve-result/batch — đồng bộ kết quả DAT HÀNG LOẠT cho
   * một mảng registrationCode (FE nên chia nhỏ ≤ ~5/chunk để có tiến độ +
   * chống rate-limit). onlyPass=true chỉ đẩy HV "Đã đủ DAT"; force=true ghi
   * đè cả HV đã đồng bộ. Trả { data: { total, counts, results } }.
   */
  async approveDatResultBatch(registrationCodes, { onlyPass = true, force = false } = {}) {
    return this.dat.post('/v2/dat/approve-result/batch', {
      center: this.center,
      registrationCodes,
      onlyPass,
      force,
    });
  }

  // ---- external integrations (token rotation) ---------------------------
  //
  // DAT external portals (DTLX MOT_DAT, TP Ecotek, SLX Jira) issue
  // captcha-gated, non-refreshable JWTs. When one expires, an admin pastes
  // a fresh token here. See uniapp controllers/dat-integration.controller.js.

  /** GET /v2/dat/integrations?center=… — list integrations + expiry status (token masked). */
  async getIntegrations() {
    return this.dat.get('/v2/dat/integrations', { params: { center: this.center } });
  }

  /** PATCH /v2/dat/integrations/:id/token — rotate the cached JWT (admin only). */
  async updateIntegrationToken(id, token) {
    return this.dat.patch(`/v2/dat/integrations/${id}/token`, {
      center: this.center,
      token,
    });
  }

  /**
   * GET /v2/dat/integrations/dtlx/session — lấy 1 phiên DAT từ cổng DRVN
   * (DTLX) theo mã phiên, để đối chiếu với dữ liệu nội bộ. Trả
   * { data: <session|null> }. Cần maPhienHoc + ngày (date hoặc dateFrom/dateTo,
   * định dạng YYYY-MM-DD).
   */
  async getDtlxSession({ maPhienHoc, date, dateFrom, dateTo }) {
    return this.dat.get('/v2/dat/integrations/dtlx/session', {
      params: { center: this.center, maPhienHoc, date, dateFrom, dateTo },
    });
  }

  /**
   * Gọi endpoint trả về FILE (.docx) thay vì envelope JSON.
   *
   * Khác mọi method còn lại nên phải xử lý riêng:
   *  - `responseType: 'blob'` + bỏ qua interceptor unwrap.
   *  - BE báo lỗi bằng JSON, nhưng vì responseType=blob nên axios đưa về
   *    một Blob → phải đọc ra mới biết nội dung lỗi, không thì người dùng
   *    tải về file rỗng mà không hiểu vì sao.
   *
   * @returns {{ blob: Blob, filename: string, headers: object }}
   */
  async _postForFile(url, form, fallbackName) {
    let res;
    try {
      res = await this.dat.post(url, form, {
        responseType: 'blob',
        transformResponse: [(d) => d],
      });
    } catch (err) {
      // Đọc Blob lỗi ra rồi gắn lại vào err.response.data để callsite
      // hiển thị đúng thông báo (vẫn giữ err.response.status).
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          err.response.data = parsed;
          err.message = parsed?.message || err.message;
        } catch (_) { /* không phải JSON → giữ nguyên lỗi gốc */ }
      }
      throw err;
    }

    const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data]);

    // Status 2xx nhưng body là JSON = BE trả lỗi có chủ đích.
    if (blob.type && blob.type.includes('json')) {
      const text = await blob.text();
      let msg = 'Xuất file thất bại.';
      try { msg = JSON.parse(text)?.message || msg; } catch (_) { /* noop */ }
      throw new Error(msg);
    }

    const headers = res?._headers || {};
    return { blob, filename: headers.filename || fallbackName, headers };
  }

  /**
   * Xuất "Biên bản tổng hợp kết quả kiểm tra" từ file điểm .xlsx.
   *
   * @param {File} file            file .xlsx người dùng chọn
   * @param {boolean} withPortrait  có kèm ảnh chân dung không (cần quyền
   *                                admin trung tâm; thiếu quyền BE trả 403)
   */
  async exportBbthReport(file, withPortrait = true) {
    const form = new FormData();
    form.append('file', file);
    form.append('withPortrait', String(withPortrait));

    const r = await this._postForFile('/v2/dat/report/bbth', form,
      'bien-ban-tong-hop.docx');
    return {
      blob: r.blob,
      filename: r.filename,
      studentCount: Number(r.headers.studentCount || 0),
      portraitCount: Number(r.headers.portraitCount || 0),
    };
  }

  /**
   * Xuất "Báo cáo quá trình đào tạo thực hành lái xe" từ file điểm .xlsx.
   *
   * @param {File} file      file .xlsx
   * @param {'file'|'system'} source  nguồn cho 5 số liệu DAT:
   *        'file'   — đọc 5 cột "DAT - *" trong xlsx (chỉ có số ở học
   *                   viên đã được bấm đồng bộ GetFly)
   *        'system' — BE tính từ dat_sessions, đầy đủ hơn nhưng chậm hơn
   *                   (1 truy vấn/học viên)
   */
  async exportPracticeReport(file, source = 'file') {
    const form = new FormData();
    form.append('file', file);
    form.append('source', source);
    const r = await this._postForFile('/v2/dat/report/practice', form,
      'bao-cao-thuc-hanh.docx');
    return {
      blob: r.blob,
      filename: r.filename,
      studentCount: Number(r.headers.studentCount || 0),
      systemCount: Number(r.headers.systemCount || 0),
    };
  }

  /**
   * Ghép danh sách học viên (.xlsx) với tổng hợp DAT → `hoc_vien_merged.xlsx`.
   *
   * Thay cho pipeline Python `dat-report`, vốn bắt người dùng tải cả `dat.json`
   * (118MB) về máy rồi chạy tay. Các phiên trong file đó chính là dữ liệu DAT
   * của hệ thống, nên BE đọc thẳng từ DB — đã đối chiếu 7.933/7.933 học viên
   * khớp tuyệt đối với bản Python.
   *
   * @param {File} file danh sách học viên .xlsx (mã học viên ở cột B)
   */
  async exportDatSummaryReport(file) {
    const form = new FormData();
    form.append('file', file);
    const r = await this._postForFile('/v2/dat/report/dat-tong-hop', form,
      'hoc-vien-tong-hop-dat.xlsx');
    return {
      blob: r.blob,
      filename: r.filename,
      studentCount: Number(r.headers.studentCount || 0),
      matchedCount: Number(r.headers.matchedCount || 0),
      sessionCount: Number(r.headers.sessionCount || 0),
    };
  }

  // ---- removed in V2 -----------------------------------------------------
  //
  // login(): V1 had its own /dat/login with hardcoded passwords. V2
  //   delegates to uniapp's existing JWT login (Zalo OTP / Google OAuth /
  //   admin password) — that auth is shared across the whole uniapp
  //   ecosystem so a single JWT works for both the LMS and DAT.
  //   Call uniapp's POST /account/login from your existing login flow
  //   and use the returned JWT here as Bearer.
  //
  // getPlate(): V1 returned the entire `plate` collection. V2 doesn't
  //   ship a public list endpoint for plates — DatVehiclePlate is
  //   referenced internally during sync transform. If a UI needs a
  //   plate dropdown, request a new endpoint (POST in next migration).
}

const datV2 = new DatServiceV2();
export default datV2;
